import {
  createContext, useContext, useEffect, useMemo
} from 'react';

import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';

import { useDashboardPath } from '#client/hooks/useDashboardPath';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';
import { PRODUCTS_KEY } from '#client/pages/Products/constants';

import { useProductDetails } from './useProductDetails';
import { useFormationStatus } from '../hooks/useFormationStatus';

const ProductLandingContext = createContext();

const useProductLandingContext = () => {
  const context = useContext(ProductLandingContext);

  if (!context) {
    throw new Error('useProductLanding must be used within a ProductLandingContext.Provider');
  }

  return context;
};

const ProductLandingProvider = ({ children, productId }) => {
  const navigate = useNavigate();
  const productsPath = useDashboardPath(`/${PRODUCTS_KEY}`);
  const productConfig = useProductDetails(productId);
  const formationStatus = useFormationStatus();
  const { entitlements } = useBusinessEntity();
  const { orderTasksWithStatus } = useOrderTrackerStatus();

  useEffect(() => {
    if (!productConfig) {
      // Redirect to products list page when product is not found
      navigate(productsPath);
    }
  }, [navigate, productsPath, productConfig]);

  // Extract hero configuration with defaults
  const heroConfig = useMemo(() => {
    const hero = productConfig?.hero;
    if (!hero) return {};

    return {
      packages: hero.buyBox || [],
      comparePackages: hero.comparePackages,
      plans: hero.plans,
      getBuyButtonConfig: productConfig.getBuyButtonConfig || (() => ({
        isBuyEnabled: false,
        ctaText: 'productPage.common.buyBox.buttonDisabled.label',
        ctaDisabledTooltip: 'productPage.common.buyBox.buttonDisabled.tooltip'
      }))
    };
  }, [productConfig]);

  const contextValue = useMemo(() => ({
    productConfig,
    productId,
    formationStatus,
    entitlements,
    orderTasksWithStatus,
    ...heroConfig
  }), [productConfig, productId, formationStatus, entitlements, heroConfig, orderTasksWithStatus]);

  return (
    <ProductLandingContext.Provider value={contextValue}>
      {children}
    </ProductLandingContext.Provider>
  );
};

ProductLandingProvider.propTypes = {
  children: PropTypes.node.isRequired,
  productId: PropTypes.string.isRequired
};

export { ProductLandingProvider, useProductLandingContext };
